'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconMoodSad } from '@tabler/icons-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import qs from 'qs'

import {
  CustomerTypes,
  extractCustomerFromClientSession,
  extractTokenFromClientSession
} from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import BackToTopButton from 'shared/ui/BackToTopButton'
import SelectDropdown from 'shared/ui/form/Select'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import LoadingOverlay from 'shared/ui/LoadingOverlay'
import { theme } from 'shared/ui/utils/designTokens'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { viewProductList } from '../analytics'
import { getNextAllProductsPage } from '../api/products'
import ProductCard from '../components/ProductCard'
import useInfiniteScroll from '../hooks/useInfiniteScroll'
import {
  FilterChangeProvider,
  useFilterChange
} from '../providers/FilterChangeContext'
import { ListingCategoryType } from '../types/categories'
import { ProductTypes } from '../types/products'
import { parseQueryFilters } from '../utils/filters'
import {
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForUpselling,
  SORTING_OPTIONS
} from '../utils/sorting'

import styles from './ProductsPage.module.scss'

interface ProductsPageContentProps {
  products: ProductTypes[]
  categories: ListingCategoryType[]
  distributionCenterId: string
}
const ProductsPageContent = ({
  products,
  distributionCenterId
}: ProductsPageContentProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const token = extractTokenFromClientSession(session)
  const [isLoading, setIsLoading] = useState(false)
  const { lastFilterChange, setLastFilterChange } = useFilterChange()
  const searchParams = useSearchParams()

  const [sorting, setSorting] = useState<string | string[]>('recommended')

  useEffect(() => {
    if (lastFilterChange) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [lastFilterChange])

  useEffect(() => {
    setIsLoading(false)
  }, [pathname, searchParams])

  useEffect(() => {
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const sortingToCheck = {
      sort_by: currentParams.sort_by,
      order: currentParams.order
    }

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [searchParams, sorting])

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(products)) {
      viewProductList({
        user,
        products,
        listName: SeoListName.ALL_PRODUCTS_PAGE_LIST_NAME
      })
    }
  }, [session, products, user])

  const query = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  )

  const { ref, productsData, isFetching } = useInfiniteScroll({
    products,
    query,
    slug: '',
    key: 'AllProductsListing',
    distributionCenterId,
    token,
    getNextPageCallback: getNextAllProductsPage,
    limit: PRODUCTS_LISTING_LIMIT
  })

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)
    setLastFilterChange(new Date())

    const sortingOptions = getSortingOptionsBySelectValueForUpselling(value)
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const parsedQuery = parseQueryFilters(currentParams)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.push(`${pathname}${decodeURIComponent(params)}`)
  }

  return (
    <div className={styles.wrapper}>
      <BackToTopButton target='*[data-title="top"]' />
      {isLoading && <LoadingOverlay />}
      <span data-title='top' />

      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Wszystkie Produkty</h1>
          <div className={styles.sortingInputWrapper} {...addTestId('sorting-input')}>
            <div className={styles.sortingLabel}>Sortuj:</div>
            <SelectDropdown
              id='products-page-sorting'
              variant='ghost'
              noMargin
              options={SORTING_OPTIONS}
              name='sorting'
              value={sorting}
              onChange={handleSortingChange}
            />
          </div>
        </div>
        <div className={styles.listingWrapper}>
          <>
            {productsData && productsData.length > 0 ? (
              <div className={styles.productsListWrapper}>
                <div className={styles.content} role='list' aria-label='Lista produktów'>
                  {productsData.map((product: ProductTypes, idx: number) => (
                    <ProductCard
                      productPath={product?.slug || ''}
                      key={product.id + idx.toString()}
                      product={product}
                      seoButtonPlace={pathname}
                      listName={SeoListName.ALL_PRODUCTS_PAGE_LIST_NAME}
                      idxOnList={idx}
                      id={`category_page_product_${product.id}`}
                    />
                  ))}
                  <InfiniteScrollAnchor ref={ref} />
                </div>
                {isFetching && (
                  <div className={styles.loaderWrapper}>
                    <LoadingIndicator
                      color={theme.colors.brand.secondary}
                      size={80}
                    />
                  </div>
                )}
              </div>
            ) : isFetching ? (
              <div className={styles.loaderWrapper}>
                <LoadingIndicator
                  color={theme.colors.brand.secondary}
                  size={80}
                />
              </div>
            ) : (
              <div className={styles.emptyStateWrapper}>
                <IconMoodSad
                  width={120}
                  size={120}
                  strokeWidth={1}
                  color={theme.colors.brand.secondary}
                />
                <div>
                  <div className={styles.emptyStateTitle}>Brak wyników</div>
                  <div className={styles.emptyStateDescription}>
                    Niestety nie udało nam się znaleźć odpowiednich produktów.
                    <br />
                    Spróbuj dostosować filtry lub sprawdź w innej kategorii.
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  )
}

const ProductsPage = (props: ProductsPageContentProps) => {
  return (
    <FilterChangeProvider>
      <ProductsPageContent {...props} />
    </FilterChangeProvider>
  )
}

export default ProductsPage
